import type { CopySightResult, CopySightStatus } from '@/types/copysight';
import { cn } from '@/lib/utils';
import { Loader2, ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';

type Tone = 'green' | 'red' | 'gray';

const TONES: Record<Tone, string> = {
  green: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/30',
  red: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30',
  gray: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/30',
};

const FLAGGED_TEXT: Partial<Record<CopySightStatus, string>> = {
  ip_found: 'IP detected',
  face_found: 'Face detected',
  ip_and_face: 'IP and face detected',
};

interface Label {
  tone: Tone;
  text: string;
  Icon: typeof ShieldCheck;
  spin?: boolean;
}

const getLabel = (result: CopySightResult | undefined, loading: boolean): Label => {
  if (!result) {
    return loading
      ? { tone: 'gray', text: 'Checking CopySight result...', Icon: Loader2, spin: true }
      : { tone: 'gray', text: 'Not checked by CopySight', Icon: ShieldQuestion };
  }
  switch (result.status) {
    case 'clean':
      return { tone: 'green', text: 'Checked: no IP detected', Icon: ShieldCheck };
    case 'error':
      return { tone: 'gray', text: 'Check failed', Icon: ShieldQuestion };
    case 'skipped':
      return { tone: 'gray', text: 'Not checked (file type not supported)', Icon: ShieldQuestion };
    default:
      return { tone: 'red', text: `Checked: ${FLAGGED_TEXT[result.status] ?? 'flagged'}`, Icon: ShieldAlert };
  }
};

interface CopySightResultLabelProps {
  result?: CopySightResult;
  // The enriched result is still being requested
  loading?: boolean;
  className?: string;
}

// "Checked / not checked" strip shown under an image: green when clean, red when
// something was flagged, gray when there is no usable result.
export default function CopySightResultLabel({ result, loading = false, className }: CopySightResultLabelProps) {
  const { tone, text, Icon, spin } = getLabel(result, loading);

  return (
    <div
      role="status"
      className={cn('flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium', TONES[tone], className)}
    >
      <Icon className={cn('h-4 w-4 shrink-0', spin && 'animate-spin')} />
      <span>{text}</span>
      <span className="ml-auto text-xs font-normal opacity-70">CopySight</span>
    </div>
  );
}
